import React, { useState } from 'react';

import { isAfter, isEqual } from 'date-fns';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  DateInput,
  DatePicker,
  FormField,
  Heading,
  Label,
  Modal,
  Paragraph,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import Page from '@renter-hub/components/RenterPage';
import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import faqs from '@common/constants/faqs';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import NotificationContainer from '@common/components/NotificationContainer';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import { PaymentAuthorizationTerms } from '@common/components/PaymentAuthorizationTerms/PaymentAuthorizationTerms';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';
import userPersonas from '@common/constants/userPersonas';
import { formatDate, getLastSelectablePaymentDate } from '@common/paymentsUtils/dateUtils';
import type { RenterMoveInCostsDetailsInterface, RenterPaymentMethodInterface } from '@renter-hub/models/Types';
import TransactionConstants from '@common/constants/TransactionConstants';
import MoveInCostsItemsDisplay from '@renter-hub/components/MoveInCostsItemsInfo';
import { MOVE_IN_COSTS_PAYMENT } from '@renter-hub/constants/paymentConstants';
import type { ScheduleMoveInCostsWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';

interface Props {
  today: Date;
  fromDashboard: boolean;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  onSubmit: (...properties: any) => any;
  togglePrevPage: () => void;
  goToDashboard: () => void;
  goToPaymentSettingPage: () => void;
  failedPaymentEmail: boolean;
  encodedPaymentMethodId: string;
  onAddNewPaymentClick: () => void;
  newPaymentMethodId: string;
  user: ZrmClickstreamUser;
  paymentMethods: Array<RenterPaymentMethodInterface>;
  listingAlias?: string;
}

const overDueText =
  'This is after the payment date your landlord chose. Make sure they’re okay with getting the payment later.';

const MoveInCostsSchedulingPage = ({
  today = new Date(),
  fromDashboard = false,
  moveInCostsDetails,
  togglePrevPage: _togglePrevPage,
  goToPaymentSettingPage,
  failedPaymentEmail = false,
  encodedPaymentMethodId = '',
  onAddNewPaymentClick,
  newPaymentMethodId,
  user,
  paymentMethods,
  listingAlias,
  ...props
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    moveInCostsDetails.isScheduled
      ? formatDate(moveInCostsDetails.sendDate, 'MM/dd/yyyy')
      : formatDate(moveInCostsDetails.dueDate, 'MM/dd/yyyy'),
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedPaymentMethodEncodedId, setSelectedPaymentMethodEncodedId] = useState(
    newPaymentMethodId || moveInCostsDetails.paymentMethodId || encodedPaymentMethodId,
  );
  const [showMultiplePaymentMethodSelectorModal, setShowMultiplePaymentMethodSelectorModal] = useState(true);

  const changeSelectedDate = (newSelectedDate: string) => {
    setSelectedDate(formatDate(newSelectedDate, 'MM/dd/yyyy'));
  };

  const isMICPaid = moveInCostsDetails.status === TransactionConstants.STATUS.COMPLETE;

  const isOverdue = () => {
    const left = coerceToDate(today);
    const right = coerceToDate(moveInCostsDetails.dueDate);
    return !!left && !!right ? isAfter(left, right) : false;
  };

  const isScheduleDateUnchanged = () => {
    const { sendDate, isScheduled } = moveInCostsDetails;
    return isScheduled && sendDate === selectedDate;
  };

  const goToDashboard = () => {
    RachelCSEvents.emitClickGoToDashboardEvent({
      user,
      currentProperty: { listingAlias },
      triggerObject: 'move_in_costs_page',
    });
    props.goToDashboard();
  };

  const onSubmit = async (triggerSource: string) => {
    setIsLoading(true);

    const paymentDate = new Date(selectedDate);
    const payload: ScheduleMoveInCostsWebApiRequestDto = {
      paymentDate: {
        year: paymentDate.getFullYear(),
        month: paymentDate.getMonth() + 1,
        day: paymentDate.getDate(),
      },
      groupKey: moveInCostsDetails.groupKey,
      encodedPaymentMethodId: selectedPaymentMethodEncodedId,
    };

    const response = await props.onSubmit(payload);
    const { error } = response;
    if (error) {
      setIsLoading(false);
    } else {
      if (fromDashboard) {
        RachelCSEvents.emitClickSendFromMoveInCostsPageDashboardFlowEvent({
          user,
          trigger_source: triggerSource,
        });
      } else {
        RachelCSEvents.emitClickContinueFromMoveInCostsPageEvent({
          user,
          currentProperty: { listingAlias },
        });
      }
    }
  };

  const renderDatePicker = () => {
    const getLastSelectableDate = getLastSelectablePaymentDate(moveInCostsDetails.dueDate);
    const minDate = new Date();
    minDate.setDate(new Date().getDate() - 1);
    return (
      <FormField css={{ width: '100%' }} label={<Label>Payment date</Label>}>
        <DatePicker
          closeModalOnDateSelect
          dateFormat="MM/dd/yyyy"
          disabledDates={(date: Date) => date < minDate || date > getLastSelectableDate}
          onOpenChange={(open) => setIsCalendarOpen(open)}
          open={isCalendarOpen}
          input={
            <DateInput
              css={{ paddingInlineStart: 'default' }}
              value={selectedDate}
              readOnly
              onClick={() => setIsCalendarOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsCalendarOpen(true);
                }
              }}
            />
          }
          onValueChange={(value) => changeSelectedDate(String(value))}
          value={selectedDate}
        />
      </FormField>
    );
  };

  const renderPaymentDate = () => {
    if (!isMICPaid && !failedPaymentEmail && !isOverdue()) {
      return (
        <>
          {renderDatePicker()}
          {isPaymentDateAfterDueDate() && <Alert tone="warning">{overDueText}</Alert>}
        </>
      );
    }
    return (
      <>
        {isMICPaid && <Alert tone="success">Your move-in payment was sent</Alert>}
        {!isMICPaid && failedPaymentEmail && (
          <Alert tone="critical">
            We couldn’t process your payment using your payment method. Contact your bank, or try a different payment
            method.
          </Alert>
        )}
        {!isMICPaid && !failedPaymentEmail && <Alert tone="critical">Your move-in payment is overdue</Alert>}
        <Box>
          <Paragraph textStyle="body-bold">{isMICPaid ? 'Paid on' : 'Pay on'}</Paragraph>
          <Text>{formatDate(selectedDate, 'MMM d, yyyy')}</Text>
        </Box>
      </>
    );
  };

  const isPaymentDateAfterDueDate = () => {
    const left = coerceToDate(selectedDate);
    const right = coerceToDate(moveInCostsDetails.dueDate);
    return !!left && !!right && isAfter(left, right) && !isOverdue();
  };

  const renderMoveInCostsSection = (moveInCostsDetails: RenterMoveInCostsDetailsInterface) => {
    let zgFee = moveInCostsDetails.zgFee;
    let totalAmount = moveInCostsDetails.totalAmount;

    const selectedPaymentMethodDetails = paymentMethods.find(
      (paymentMethod) => paymentMethod.id === selectedPaymentMethodEncodedId,
    );

    const oneTimePaymentDetails = selectedPaymentMethodDetails?.oneTimePaymentAmountDetails.find(
      (oneTimePayment) => oneTimePayment.groupKey === moveInCostsDetails.groupKey,
    );

    if (oneTimePaymentDetails) {
      zgFee = oneTimePaymentDetails.fee;
      totalAmount = oneTimePaymentDetails.totalAmount;
    }

    return (
      <Card.Root elevated={false}>
        <Card.Header>
          <Paragraph textStyle="body-bold">Move-in payment</Paragraph>
          <Paragraph className="fs-exclude">Due on {formatDate(moveInCostsDetails.dueDate, 'MMMM d, yyyy')}</Paragraph>
          {isMICPaid && <Tag tone="success">Paid</Tag>}
        </Card.Header>
        <Card.Body>
          <MoveInCostsItemsDisplay zgFee={zgFee} totalAmount={totalAmount} items={moveInCostsDetails.items} />
        </Card.Body>
      </Card.Root>
    );
  };

  const handleContinueButtonClick = () => {
    if (!fromDashboard) {
      RachelCSEvents.emitClickContinueFromMoveInCostsPageEvent({
        user,
        currentProperty: { listingAlias },
      });
    }
    goToPaymentSettingPage();
  };

  const willAuthorizePayment = !isMICPaid && !isScheduleDateUnchanged();

  const renderNavButtons = () => {
    const left = coerceToDate(selectedDate);
    const right = coerceToDate(formatDate(today, 'yyyy-MM-dd'));
    const isSelectedDateToday = !!left && !!right ? isEqual(left, right) : false;
    let paymentButtonText;
    let triggerSource;
    if (failedPaymentEmail) {
      paymentButtonText = 'Try again';
      triggerSource = 'try_again_button';
    } else if (isOverdue() || isSelectedDateToday) {
      paymentButtonText = 'Send move-in payment now';
      triggerSource = 'send_now_button';
    } else {
      paymentButtonText = 'Schedule move-in payment';
      triggerSource = 'schedule_button';
    }
    return (
      <>
        {!fromDashboard && <Button onClick={goToDashboard}>Go to dashboard</Button>}
        {fromDashboard && <TextButton onClick={() => setShowMultiplePaymentMethodSelectorModal(true)}>Back</TextButton>}
        {!willAuthorizePayment ? (
          <Button emphasis="filled" onClick={handleContinueButtonClick}>
            Continue
          </Button>
        ) : (
          <DebouncedButton emphasis="filled" onClick={() => onSubmit(triggerSource)} loading={isLoading}>
            {paymentButtonText}
          </DebouncedButton>
        )}
      </>
    );
  };

  const renderFaqSection = () => <FaqComponent faqs={faqs.MOVE_IN_COSTS_SCHEDULING_PAGE.body()} />;

  const renderMultiplePaymentMethodSelectorModal = () => {
    return (
      <Modal
        open={true}
        trigger={null}
        size="md"
        header={<Heading level={2}>Select a payment method</Heading>}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            goToDashboard();
          }
        }}
        body={
          <>
            <>
              <AvailablePaymentMethods
                onPaymentMethodClick={(paymentMethod) => setSelectedPaymentMethodEncodedId(paymentMethod.id)}
                renderRadioView={true}
                selectedPaymentMethodId={selectedPaymentMethodEncodedId}
              />
            </>
            <TextButton onClick={onAddNewPaymentClick}>Add new payment method</TextButton>
          </>
        }
        footer={
          <ButtonGroup aria-label="Modal actions">
            <TextButton textStyle="body-bold" onClick={goToDashboard}>
              Back
            </TextButton>
            <Button
              disabled={!selectedPaymentMethodEncodedId}
              emphasis="filled"
              onClick={() => {
                RachelCSEvents.emitClickContinueFromSelectPaymentMethodModalEvent({ user });
                if (selectedPaymentMethodEncodedId) {
                  setShowMultiplePaymentMethodSelectorModal(false);
                }
              }}
            >
              Continue
            </Button>
          </ButtonGroup>
        }
      />
    );
  };

  const renderMainSection = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'loose' }}>
      <NotificationContainer persona={userPersonas.RENTER} />
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>{renderPaymentDate()}</Box>
      <Box>
        <Text textStyle="body-sm">
          If this date falls on a weekend or holiday, we’ll send the payment <strong>1 business day later</strong>.
        </Text>
      </Box>
      {renderMoveInCostsSection(moveInCostsDetails)}
      <Alert tone="info">If you’ve taken care of this payment, please ask your landlord to mark it as paid</Alert>
      {!fromDashboard && (
        <ButtonGroup fluid aria-label="Move-in payment actions">
          {renderNavButtons()}
        </ButtonGroup>
      )}
      {willAuthorizePayment && <PaymentAuthorizationTerms variant="oneTime" />}
    </Box>
  );

  const renderHeader = () => (
    <Heading level={2} textStyle="heading-xs">
      {isMICPaid ? 'Your move-in payment has been taken care of' : 'When would you like to make your move-in payment?'}
    </Heading>
  );

  const renderPage = () => {
    const progressBarProps = { progressTitle: MOVE_IN_COSTS_PAYMENT };
    return (
      <Page {...progressBarProps}>
        <HelmetContainer {...SeoConstants.moveInCostsSchedulingPage} />
        <Page.MainSection>
          <Box css={{ marginBottom: 'default' }}>{renderHeader()}</Box>
          {renderMainSection()}
        </Page.MainSection>
        <Page.RightSection>{renderFaqSection()}</Page.RightSection>
        <Page.BottomSection>{renderFaqSection()}</Page.BottomSection>
      </Page>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        trigger={null}
        open={true}
        size="sm"
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            goToDashboard();
          }
        }}
        header={renderHeader()}
        body={
          <>
            <HelmetContainer {...SeoConstants.paymentInfoPage} />
            {renderMainSection()}
            {renderFaqSection()}
          </>
        }
        footer={<ButtonGroup aria-label="Move-in payment actions">{renderNavButtons()}</ButtonGroup>}
      />
    );
  };

  if (fromDashboard) {
    if (showMultiplePaymentMethodSelectorModal) {
      return renderMultiplePaymentMethodSelectorModal();
    }

    return renderModal();
  }

  return renderPage();
};

export default MoveInCostsSchedulingPage;
